import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiLoader,
} from "react-icons/fi";

import {
    fetchSaleInvoiceById,
} from "../../../../../../store/billing";

import API from "../../../../../../utils/apiCalling";
import {
    config,
} from "../../../../../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| COMPONENTS
|--------------------------------------------------------------------------
*/

import LedgerHeader from "./components/LedgerHeader";
import LedgerSummaryCards from "./components/LedgerSummaryCards";
import LedgerTabs, {
    LEDGER_TABS,
} from "./components/LedgerTabs";
import LedgerToolbar from "./components/LedgerToolbar";
import LedgerEntriesTable from "./components/LedgerEntriesTable";
import CustomerWiseLedger from "./components/CustomerWiseLedger";
import CustomerLedgerDialog from "./components/CustomerLedgerDialog";
import LedgerPagination from "./components/LedgerPagination";

import SaleInvoicePaymentDialog, {
    getBalanceAmount,
} from "../SaleInvoices/SaleInvoicePaymentDialog";
/*
|--------------------------------------------------------------------------
| UTILS
|--------------------------------------------------------------------------
*/

import {
    calculateLedgerSummary,
    getCustomerId,
    getEntryType,
    groupLedgerByCustomer,
    matchesLedgerSearch,
    normalizeLedgerResponse,
    normalizeText,
    toNumber,
} from "./utils/customerLedgerUtils";
/*
|--------------------------------------------------------------------------
| CSS
|--------------------------------------------------------------------------
*/

import "./CustomerLedgerPage.css";
import useSwal from "../../../../../../hooks/useSwal";
import { useDispatch } from "react-redux";

const api =
    new API();

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CustomerLedgerPage =
    () => {
        const dispatch =
            useDispatch();
        /*
        |--------------------------------------------------------------------------
        | API DATA
        |--------------------------------------------------------------------------
        */
        const swal = useSwal()
        const [
            records,
            setRecords,
        ] = useState([]);

        const [
            apiSummary,
            setApiSummary,
        ] = useState({});

        const [
            loading,
            setLoading,
        ] = useState(false);

        const [
            error,
            setError,
        ] = useState("");


        const [
            paymentInvoice,
            setPaymentInvoice,
        ] = useState(null);

        const [
            paymentDialogOpen,
            setPaymentDialogOpen,
        ] = useState(false);
        /*
        |--------------------------------------------------------------------------
        | VIEW
        |--------------------------------------------------------------------------
        */

        const [
            activeTab,
            setActiveTab,
        ] = useState(
            LEDGER_TABS.ALL
        );

        /*
        |--------------------------------------------------------------------------
        | FILTERS
        |--------------------------------------------------------------------------
        */

        const [
            search,
            setSearch,
        ] = useState("");

        const [
            entryType,
            setEntryType,
        ] = useState("");

        /*
        |--------------------------------------------------------------------------
        | ALL ENTRIES PAGINATION
        |--------------------------------------------------------------------------
        */

        const [
            page,
            setPage,
        ] = useState(1);

        const [
            pageSize,
            setPageSize,
        ] = useState(20);

        /*
        |--------------------------------------------------------------------------
        | CUSTOMER PAGINATION
        |--------------------------------------------------------------------------
        */

        const [
            customerPage,
            setCustomerPage,
        ] = useState(1);

        const [
            customerPageSize,
            setCustomerPageSize,
        ] = useState(12);

        /*
        |--------------------------------------------------------------------------
        | SELECTED CUSTOMER
        |--------------------------------------------------------------------------
        */

        const [
            selectedCustomer,
            setSelectedCustomer,
        ] = useState(null);

        const [
            customerDialogOpen,
            setCustomerDialogOpen,
        ] = useState(false);

        /*
        |--------------------------------------------------------------------------
        | FETCH LEDGER
        |--------------------------------------------------------------------------
        |
        | We fetch a larger set because:
        |
        | 1. All Entries tab needs transactions
        | 2. Customer Wise tab groups those entries customer-wise
        |
        | Later this can be changed to dedicated backend customer-summary
        | pagination if the ledger becomes very large.
        |--------------------------------------------------------------------------
        */

        const loadLedger =
            useCallback(
                async () => {
                    try {
                        setLoading(
                            true
                        );

                        setError(
                            ""
                        );

                        const query =
                            new URLSearchParams();

                        query.append(
                            "page",
                            "1"
                        );

                        query.append(
                            "limit",
                            "500"
                        );

                        const response =
                            await api.get(
                                `${config.getAllCustomerLedger()}?${query.toString()}`
                            );

                        const normalized =
                            normalizeLedgerResponse(
                                response
                            );

                        setRecords(
                            Array.isArray(
                                normalized
                                    ?.records
                            )
                                ? normalized.records
                                : []
                        );

                        setApiSummary(
                            normalized
                                ?.summary ||
                            {}
                        );
                    } catch (
                    requestError
                    ) {
                        console.error(
                            "Customer ledger error:",
                            requestError
                        );

                        setRecords(
                            []
                        );

                        setApiSummary(
                            {}
                        );

                        setError(
                            requestError
                                ?.response
                                ?.data
                                ?.message ||
                            requestError
                                ?.response
                                ?.data
                                ?.error ||
                            requestError
                                ?.message ||
                            "Unable to load customer ledger."
                        );
                    } finally {
                        setLoading(
                            false
                        );
                    }
                },
                []
            );

        useEffect(
            () => {
                loadLedger();
            },
            [
                loadLedger,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | ALL ENTRIES FILTER
        |--------------------------------------------------------------------------
        */

        const filteredEntries =
            useMemo(
                () => {
                    return records.filter(
                        (
                            row
                        ) => {
                            const type =
                                getEntryType(
                                    row
                                );

                            if (
                                entryType &&
                                type !==
                                entryType
                            ) {
                                return false;
                            }

                            return matchesLedgerSearch(
                                row,
                                search
                            );
                        }
                    );
                },
                [
                    records,
                    search,
                    entryType,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | GLOBAL SUMMARY
        |--------------------------------------------------------------------------
        */

        const calculatedSummary =
            useMemo(
                () =>
                    calculateLedgerSummary(
                        records
                    ),
                [
                    records,
                ]
            );

        const totalDebit =
            apiSummary
                ?.totalDebit !==
                undefined
                ? toNumber(
                    apiSummary
                        .totalDebit
                )
                : calculatedSummary
                    .totalDebit;

        const totalCredit =
            apiSummary
                ?.totalCredit !==
                undefined
                ? toNumber(
                    apiSummary
                        .totalCredit
                )
                : calculatedSummary
                    .totalCredit;

        const outstanding =
            apiSummary
                ?.netBalance !==
                undefined
                ? toNumber(
                    apiSummary
                        .netBalance
                )
                : apiSummary
                    ?.balance !==
                    undefined
                    ? toNumber(
                        apiSummary
                            .balance
                    )
                    : calculatedSummary
                        .netBalance;

        /*
        |--------------------------------------------------------------------------
        | CUSTOMER GROUPING
        |--------------------------------------------------------------------------
        */

        const customerLedgers =
            useMemo(
                () =>
                    groupLedgerByCustomer(
                        records
                    ),
                [
                    records,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | CUSTOMER SEARCH
        |--------------------------------------------------------------------------
        */

        const filteredCustomers =
            useMemo(
                () => {
                    const keyword =
                        normalizeText(
                            search
                        );

                    if (
                        !keyword
                    ) {
                        return customerLedgers;
                    }

                    return customerLedgers.filter(
                        (
                            customer
                        ) => {
                            const searchable =
                                [
                                    customer
                                        ?.name,

                                    customer
                                        ?.mobile,

                                    customer
                                        ?.email,

                                    customer
                                        ?.customerId,
                                ]
                                    .filter(
                                        Boolean
                                    )
                                    .join(
                                        " "
                                    )
                                    .toLowerCase();

                            return searchable.includes(
                                keyword
                            );
                        }
                    );
                },
                [
                    customerLedgers,
                    search,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | ALL ENTRY PAGINATION
        |--------------------------------------------------------------------------
        */

        const entryTotalRecords =
            filteredEntries.length;

        const entryTotalPages =
            Math.max(
                Math.ceil(
                    entryTotalRecords /
                    pageSize
                ),
                1
            );

        const paginatedEntries =
            useMemo(
                () => {
                    const start =
                        (
                            page -
                            1
                        ) *
                        pageSize;

                    return filteredEntries.slice(
                        start,
                        start +
                        pageSize
                    );
                },
                [
                    filteredEntries,
                    page,
                    pageSize,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | CUSTOMER PAGINATION
        |--------------------------------------------------------------------------
        */

        const customerTotalRecords =
            filteredCustomers.length;

        const customerTotalPages =
            Math.max(
                Math.ceil(
                    customerTotalRecords /
                    customerPageSize
                ),
                1
            );

        const paginatedCustomers =
            useMemo(
                () => {
                    const start =
                        (
                            customerPage -
                            1
                        ) *
                        customerPageSize;

                    return filteredCustomers.slice(
                        start,
                        start +
                        customerPageSize
                    );
                },
                [
                    filteredCustomers,
                    customerPage,
                    customerPageSize,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | KEEP PAGE VALID
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                if (
                    page >
                    entryTotalPages
                ) {
                    setPage(
                        entryTotalPages
                    );
                }
            },
            [
                page,
                entryTotalPages,
            ]
        );

        useEffect(
            () => {
                if (
                    customerPage >
                    customerTotalPages
                ) {
                    setCustomerPage(
                        customerTotalPages
                    );
                }
            },
            [
                customerPage,
                customerTotalPages,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | TAB CHANGE
        |--------------------------------------------------------------------------
        */

        const handleTabChange = (
            tab
        ) => {
            setActiveTab(
                tab
            );

            setSearch(
                ""
            );

            setEntryType(
                ""
            );

            setPage(
                1
            );

            setCustomerPage(
                1
            );
        };

        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const handleSearchChange = (
            value
        ) => {
            setSearch(
                value
            );

            setPage(
                1
            );

            setCustomerPage(
                1
            );
        };

        /*
        |--------------------------------------------------------------------------
        | ENTRY TYPE
        |--------------------------------------------------------------------------
        */

        const handleEntryTypeChange = (
            value
        ) => {
            setEntryType(
                value
            );

            setPage(
                1
            );
        };

        /*
        |--------------------------------------------------------------------------
        | VIEW CUSTOMER LEDGER
        |--------------------------------------------------------------------------
        */

        const handleViewCustomer = (
            customer
        ) => {
            if (
                !customer
            ) {
                return;
            }

            setSelectedCustomer(
                customer
            );

            setCustomerDialogOpen(
                true
            );
        };

        const handleCloseCustomerDialog =
            () => {
                setCustomerDialogOpen(
                    false
                );

                setSelectedCustomer(
                    null
                );
            };
        const getLedgerInvoiceId = (
            row
        ) => {
            if (!row) {
                return "";
            }

            /*
             * Your actual ledger API returns:
             *
             * document: "6a74f74b0d12ca38bd0b24df"
             *
             * but sometimes document may be populated.
             */

            if (
                row?.document &&
                typeof row.document ===
                "object"
            ) {
                return (
                    row.document?._id ||
                    row.document?.id ||
                    ""
                );
            }

            if (
                typeof row?.document ===
                "string"
            ) {
                return row.document;
            }

            return (
                row?.documentId ||
                row?.invoiceId ||
                row?.saleInvoiceId ||
                row?.invoice?._id ||
                row?.saleInvoice?._id ||
                ""
            );
        };

        const handleReceivePayment =
            async (
                customer
            ) => {
                if (!customer) {
                    return;
                }

                try {
                    const customerEntries =
                        Array.isArray(
                            customer?.entries
                        )
                            ? customer.entries
                            : [];

                    /*
                    |--------------------------------------------------------------------------
                    | FIND SALE INVOICE IDS FROM LEDGER
                    |--------------------------------------------------------------------------
                    */

                    const invoiceIds =
                        [
                            ...new Set(
                                customerEntries
                                    .filter(
                                        (
                                            row
                                        ) =>
                                            String(
                                                row
                                                    ?.documentType ||
                                                ""
                                            )
                                                .trim()
                                                .toUpperCase() ===
                                            "SALE_INVOICE"
                                    )
                                    .map(
                                        (
                                            row
                                        ) =>
                                            getLedgerInvoiceId(
                                                row
                                            )
                                    )
                                    .filter(
                                        Boolean
                                    )
                                    .map(
                                        String
                                    )
                            ),
                        ];

                    if (
                        invoiceIds.length ===
                        0
                    ) {
                        swal.error({
                            title:
                                "Invoice Not Found",

                            text:
                                "No sale invoice is linked with this customer's ledger.",
                        });

                        return;
                    }

                    /*
                    |--------------------------------------------------------------------------
                    | FETCH REAL SALE INVOICE OBJECTS
                    |--------------------------------------------------------------------------
                    */

                    const outstandingInvoices =
                        [];

                    for (
                        const invoiceId of
                        invoiceIds
                    ) {
                        try {
                            const result =
                                await dispatch(
                                    fetchSaleInvoiceById(
                                        invoiceId
                                    )
                                ).unwrap();

                            const invoice =
                                result?.invoice ||
                                result?.data
                                    ?.invoice ||
                                result?.data ||
                                result;

                            if (!invoice) {
                                continue;
                            }

                            const balance =
                                getBalanceAmount(
                                    invoice
                                );

                            if (
                                balance >
                                0
                            ) {
                                outstandingInvoices.push(
                                    invoice
                                );
                            }
                        } catch (
                        invoiceError
                        ) {
                            console.error(
                                "Unable to fetch invoice:",
                                invoiceId,
                                invoiceError
                            );
                        }
                    }

                    if (
                        outstandingInvoices.length ===
                        0
                    ) {
                        swal.error({
                            title:
                                "No Outstanding Invoice",

                            text:
                                "This customer has no outstanding sale invoice.",
                        });

                        return;
                    }

                    /*
                    |--------------------------------------------------------------------------
                    | FOR NOW OPEN FIRST OUTSTANDING INVOICE
                    |--------------------------------------------------------------------------
                    */

                    setPaymentInvoice(
                        outstandingInvoices[0]
                    );

                    setPaymentDialogOpen(
                        true
                    );
                } catch (
                error
                ) {
                    console.error(
                        "Receive payment error:",
                        error
                    );

                    swal.error({
                        title:
                            "Unable to Load Invoice",

                        text:
                            error?.message ||
                            "Unable to load outstanding invoice.",
                    });
                }
            };
        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="customer-ledger-page">
                {/* HEADER */}

                <LedgerHeader
                    loading={
                        loading
                    }
                    onRefresh={
                        loadLedger
                    }
                />

                {/* SUMMARY */}

                <LedgerSummaryCards
                    transactionCount={
                        records.length
                    }
                    customerCount={
                        customerLedgers.length
                    }
                    totalDebit={
                        totalDebit
                    }
                    totalCredit={
                        totalCredit
                    }
                    outstanding={
                        outstanding
                    }
                />

                {/* ERROR */}

                {error && (
                    <div className="customer-ledger-page-error">
                        <FiAlertCircle />

                        <div>
                            <strong>
                                Unable to load customer ledger
                            </strong>

                            <span>
                                {
                                    error
                                }
                            </span>
                        </div>
                    </div>
                )}

                {/* MAIN CARD */}

                <section className="customer-ledger-main-card">
                    <LedgerTabs
                        activeTab={
                            activeTab
                        }
                        transactionCount={
                            records.length
                        }
                        customerCount={
                            customerLedgers.length
                        }
                        onChange={
                            handleTabChange
                        }
                    />

                    <LedgerToolbar
                        activeTab={
                            activeTab
                        }
                        search={
                            search
                        }
                        entryType={
                            entryType
                        }
                        onSearchChange={
                            handleSearchChange
                        }
                        onEntryTypeChange={
                            handleEntryTypeChange
                        }
                        onClearSearch={() => {
                            setSearch(
                                ""
                            );

                            setPage(
                                1
                            );

                            setCustomerPage(
                                1
                            );
                        }}
                    />

                    {/* INITIAL LOADER */}

                    {loading &&
                        records.length ===
                        0 ? (
                        <div className="customer-ledger-page-loading">
                            <FiLoader className="customer-ledger-spinner" />

                            <strong>
                                Loading customer ledger...
                            </strong>

                            <span>
                                Preparing ledger transactions and customer balances.
                            </span>
                        </div>
                    ) : (
                        <>
                            {/* ALL ENTRIES */}

                            {activeTab ===
                                LEDGER_TABS.ALL && (
                                    <>
                                        <LedgerEntriesTable
                                            records={
                                                paginatedEntries
                                            }
                                            loading={
                                                loading
                                            }
                                        />

                                        <LedgerPagination
                                            page={
                                                page
                                            }
                                            pageSize={
                                                pageSize
                                            }
                                            totalRecords={
                                                entryTotalRecords
                                            }
                                            totalPages={
                                                entryTotalPages
                                            }
                                            onPageChange={
                                                setPage
                                            }
                                            onPageSizeChange={(
                                                value
                                            ) => {
                                                setPageSize(
                                                    value
                                                );

                                                setPage(
                                                    1
                                                );
                                            }}
                                        />
                                    </>
                                )}

                            {/* CUSTOMER WISE */}

                            {activeTab ===
                                LEDGER_TABS.CUSTOMERS && (
                                    <>
                                        <CustomerWiseLedger
                                            customers={
                                                paginatedCustomers
                                            }
                                            loading={
                                                loading
                                            }
                                            onViewCustomer={
                                                handleViewCustomer
                                            }
                                            onReceivePayment={
                                                handleReceivePayment
                                            }
                                        />

                                        <LedgerPagination
                                            page={
                                                customerPage
                                            }
                                            pageSize={
                                                customerPageSize
                                            }
                                            totalRecords={
                                                customerTotalRecords
                                            }
                                            totalPages={
                                                customerTotalPages
                                            }
                                            pageSizeOptions={[
                                                6,
                                                12,
                                                24,
                                                48,
                                            ]}
                                            onPageChange={
                                                setCustomerPage
                                            }
                                            onPageSizeChange={(
                                                value
                                            ) => {
                                                setCustomerPageSize(
                                                    value
                                                );

                                                setCustomerPage(
                                                    1
                                                );
                                            }}
                                        />
                                    </>
                                )}
                        </>
                    )}
                </section>

                {/* CUSTOMER LEDGER DETAILS */}

                <CustomerLedgerDialog
                    open={
                        customerDialogOpen
                    }
                    customer={
                        selectedCustomer
                    }
                    onClose={
                        handleCloseCustomerDialog
                    }
                />
                {paymentInvoice && (
                    <SaleInvoicePaymentDialog
                        open={
                            paymentDialogOpen
                        }
                        invoice={
                            paymentInvoice
                        }
                        onClose={() => {
                            setPaymentDialogOpen(
                                false
                            );

                            setPaymentInvoice(
                                null
                            );
                        }}
                        onSuccess={async () => {
                            setPaymentDialogOpen(
                                false
                            );

                            setPaymentInvoice(
                                null
                            );

                            await loadLedger();
                        }}
                    />
                )}
            </div>
        );
    };

export default CustomerLedgerPage;